import React from 'react';
import { Input, InputNumber, Select, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { ze } from 'utils/zhEn';
import { CaseSensitivity, QueryType } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import CustomKvSearchKey from 'components/commons/IssueCrashFilter/CustomKvSearchKey';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';

const IcfCustomKvFilter = ({
  isReadOnly,
  appId,
  isAdvancedTrend,
  fieldValue,
  onChangeFieldValue,
}) => {
  const { t } = useTranslation();

  const {
    subField,
    not,
    queryType,
    term,
    terms,
    gte,
    lte,
  } = fieldValue || {};

  const isRangeQueryTypeAvailable = /^(?:C03_)?I#.+$/.test(subField || '');
  const isWildcardQueryTypeAvailable = !isRangeQueryTypeAvailable && !isAdvancedTrend;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      <CustomKvSearchKey
        appId={appId}
        isReadOnly={isReadOnly}
        isAdvancedTrend={isAdvancedTrend}
        value={subField}
        onChange={v => onChangeFieldValue({ subField: v, queryType: QueryType.TERMS })}
      />
      { subField && <Select
        style={{ width: '120px' }}
        options={[
          { value: 0, label: ze('满足', 'Is') },
          { value: 1, label: ze('不满足', 'Is Not') },
        ]}
        value={Number(!!not)}
        onChange={v => {
          if (isReadOnly) {
            return;
          }
          onChangeFieldValue({ ...fieldValue, not: !!v });
        }}
      /> }
      { subField && <Select
        style={{ width: '120px' }}
        options={[
          { value: QueryType.TERMS, name: t('issueCrashFilter.精确匹配'), label: t('issueCrashFilter.精确匹配') },
          ...(isWildcardQueryTypeAvailable ? [{ value: QueryType.TERM_WILDCARD, name: t('issueCrashFilter.通配符匹配'), label: t('issueCrashFilter.通配符匹配') }] : []),
          ...(isRangeQueryTypeAvailable ? [{ value: QueryType.RANGE, name: t('issueCrashFilter.数值范围'), label: t('issueCrashFilter.数值范围') }] : []),
          {
            value: QueryType.NONEMPTY,
            name: ze('非空', 'Non-empty'),
            label: <Tooltip
              placement='right'
              title={ze(
                '该字段已有值且不为空字符串（即字段存在，并已被设置为非空内容）',
                'The field has a value set and it is not an empty string (i.e. the field exists and has been assigned a non-empty value).',
              )}
            >
              <Space>
                <div>{ ze('非空', 'Non-empty') }</div>
                <WrappedTipsIcon />
              </Space>
            </Tooltip>,
          },
        ]}
        optionFilterProp='name'
        optionLabelProp='name'
        value={queryType}
        onChange={v => {
          if (isReadOnly) {
            return;
          }
          onChangeFieldValue({ subField, queryType: v });
        }}
      /> }
      { subField && queryType === QueryType.TERMS && <Select
        mode='tags'
        placeholder={ze('输入一个或多个值。单击弹出的选项，或按回车键确认输入的值', 'Enter one or more values. Click the pop-up option, or press Enter to confirm the entered values.')}
        style={{ flexGrow: 1 }}
        value={terms}
        allowClear={true}
        notFoundContent={null}
        onChange={v => {
          if (isReadOnly) {
            return;
          }
          onChangeFieldValue({
            ...fieldValue,
            terms: v,
            // 设置为大小写不敏感的方式匹配，但是因为es的数值型字段不允许设置为case insensitive，所以数值型搜索不设置
            ...(isRangeQueryTypeAvailable ? {} : { caseSensitivity: CaseSensitivity.INSENSITIVE }),
          });
        }}
      /> }
      { subField && queryType === QueryType.TERM_WILDCARD && <div style={{ flexGrow: '1' }}>
        <Input
          placeholder={ze('支持通配符 * 和 ?', 'Supports two wildcard operators: * and ?')}
          value={term}
          onChange={e => {
            if (isReadOnly) {
              return;
            }
            onChangeFieldValue({ ...fieldValue, term: e.target.value });
          }}
        />
      </div> }
      { subField && queryType === QueryType.RANGE && <div style={{ flexGrow: '1' }}>
        <Input.Group compact>
          <InputNumber
            value={gte}
            readOnly={isReadOnly}
            style={{ width: 'calc(50% - 20px)', textAlign: 'center' }}
            placeholder="下限（包含）"
            onChange={v => onChangeFieldValue({ ...fieldValue, gte: v })}
          />
          <Input style={{ width: '40px', backgroundColor: 'rgba(0,0,0,0)', textAlign: 'center', pointerEvents: 'none' }} placeholder="~" disabled />
          <InputNumber
            value={lte}
            readOnly={isReadOnly}
            style={{ width: 'calc(50% - 20px)', textAlign: 'center' }}
            placeholder="上限（包含）"
            onChange={v => onChangeFieldValue({ ...fieldValue, lte: v })}
          />
        </Input.Group>
      </div> }
    </div>
  );
};

export default IcfCustomKvFilter;
